import React, { useState, useEffect, useMemo } from 'react';
import { FolderSimplePlus, X } from '@phosphor-icons/react';
import { getFileIconBySize } from '../../utils/fileUtils';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import {
  Overlay,
  Modal,
  CloseButton,
  ModalTitle,
  MergeInstructionText,
  SearchSection,
  BreadcrumbSection,
  TreeSection,
  Actions,
  CancelButton,
  ProceedButton,
  ListItem,
  ListItemContent,
  ListItemIcon,
  ListItemName,
  ListContainer,
  EmptyState,
  AngularExpandIcon,
} from './FileMergeModalStyled';
import Search from '../Search/CommonSearch';
import Breadcrumb from '../BreadCrumbComponent/BreadCrumb';
import { useSelector, useDispatch } from 'react-redux';
import { fetchBreadCrumbs } from '../../slice/MatterDetailsSlice';
import { createBreadcrumbFromTreeData } from '../TabView/TabViewUtils';
import { PDF_FILE_TYPES, DOC_FILE_TYPES, XLSX_FILE_TYPES } from '../../utils/constants';

const FileMergeModal = ({
  open,
  onClose,
  title = "Choose a File to merge",
  treeData = [],
  selectedFile = null,
  selectedFiles = [],
  onFileSelect,
  onProceed,
  onCancel,
  searchPlaceholder = "Search",
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [navigationPath, setNavigationPath] = useState([]); // Array of {title, data, id} objects
  const [selectedItem, setSelectedItem] = useState(selectedFile);
  const [initialBreadcrumb, setInitialBreadcrumb] = useState([]);
  const dispatch = useDispatch();
  const location = useLocation();
  const {matterDetails} = useSelector((state) => state.matterDetails);

  // Helper function to find item in tree by unique_id
  const findItemInTree = (tree, uniqueId) => {
    if (!tree || !Array.isArray(tree)) return null;
    
    for (const item of tree) {
      if (item.key === uniqueId || item.record?.unique_id === uniqueId) {
        return item;
      }
      if (Array.isArray(item.children)) {
        const found = findItemInTree(item.children, uniqueId);
        if (found) return found;
      }
    }
    return null;
  };

  // Helper function to find item in tree by title (for navigation path)
  const findItemInTreeByTitle = (tree, title, parentPath = []) => {
    if (!tree || !Array.isArray(tree)) return null;
    
    for (const item of tree) {
      if (item.title === title) {
        return { item, path: [...parentPath, item] };
      }
      if (Array.isArray(item.children)) {
        const found = findItemInTreeByTitle(item.children, title, [...parentPath, item]);
        if (found) return found;
      }
    }
    return null;
  };

  // Helper function to recursively collect all items from tree with their paths
  const collectAllItems = (tree, parentPath = []) => {
    if (!tree || !Array.isArray(tree)) return [];
    
    const allItems = [];
    
    for (const item of tree) {
      // Add current item with its path
      allItems.push({
        ...item,
        fullPath: [...parentPath, item]
      });
      
      // Recursively collect children
      if (Array.isArray(item.children) && item.children.length > 0) {
        const childItems = collectAllItems(item.children, [...parentPath, item]);
        allItems.push(...childItems);
      }
    }
    
    return allItems;
  };

  // Helper function to build navigation path from item's full path
  const buildNavigationPathFromItem = (item) => {
    if (!item.fullPath || item.fullPath.length === 0) return [];
    
    // Skip the item itself and build path to its parent
    // fullPath includes the item itself as the last element
    const pathToParent = item.fullPath.slice(0, -1);
    
    // Map to navigation path format, skipping root level items
    return pathToParent.map(pathItem => ({
      title: pathItem.title,
      id: pathItem.key || pathItem.record?.unique_id,
      data: pathItem
    })).filter(pathItem => pathItem.id && pathItem.id !== 0 && pathItem.id !== 'root');
  };

  // Get current level items based on breadcrumb path
  const getCurrentLevelItems = () => {
    let currentLevel = treeData;
    
    // First, navigate through initialBreadcrumb items (skip Matters[0] and Matter root[1])
    // Only navigate up to the last folder in initialBreadcrumb (exclude document if it's the last item)
    if (initialBreadcrumb.length > 2) {
      for (let i = 2; i < initialBreadcrumb.length; i++) {
        const breadcrumbItem = initialBreadcrumb[i];
        if (!breadcrumbItem || !breadcrumbItem.id || breadcrumbItem.id === 0 || breadcrumbItem.id === 'root') {
          continue;
        }
        
        // Check if this is a document (starts with 'D') - if so, stop before it
        const isDocument = breadcrumbItem.id.toString().startsWith('D');
        if (isDocument) {
          break; // Stop before the document, we want to show its parent folder
        }
        
        const found = findItemInTree(currentLevel, breadcrumbItem.id);
        if (found && Array.isArray(found.children)) {
          currentLevel = found.children;
        } else {
          // If not found by id, try by name
          const foundByName = currentLevel.find(item => item.title === breadcrumbItem.name);
          if (foundByName && Array.isArray(foundByName.children)) {
            currentLevel = foundByName.children;
          } else {
            break;
          }
        }
      }
    }
    
    // Then navigate through the navigation path (folders clicked by user)
    for (const pathItem of navigationPath) {
      const found = currentLevel.find(item => 
        item.title === pathItem.title || 
        item.key === pathItem.id || 
        item.record?.unique_id === pathItem.id
      );
      if (found && Array.isArray(found.children)) {
        currentLevel = found.children;
      } else {
        return [];
      }
    }
    
    return Array.isArray(currentLevel) ? currentLevel : [];
  };

  // Get current level items
  const currentLevelItems = useMemo(() => getCurrentLevelItems(), [treeData, navigationPath, initialBreadcrumb]);

  // Collect all items from entire tree for global search
  const allTreeItems = useMemo(() => {
    if (!treeData || !Array.isArray(treeData)) return [];
    return collectAllItems(treeData);
  }, [treeData]);

  // Get file extension from file name
  const getFileExtension = (fileName) => {
    if (!fileName) return null;
    return fileName.split(".").pop()?.toLowerCase() || null;
  };

  // Helper function to determine file type from extension or MIME type
  const getFileTypeFromRecord = (file) => {
    // First check file_type property (might be MIME type)
    if (file?.file_type) {
      if (PDF_FILE_TYPES.includes(file.file_type)) return 'pdf';
      if (DOC_FILE_TYPES.includes(file.file_type)) return 'docx';
      if (XLSX_FILE_TYPES.includes(file.file_type)) return 'xlsx';
    }
    // Fallback to extension from filename
    const fileName = file?.title || file?.name || file?.record?.title || file?.record?.name;
    return getFileExtension(fileName);
  };

  // Get the first file's name and type
  const getFirstFileInfo = useMemo(() => {
    // Check selectedFiles first (when adding more files)
    if (selectedFiles && selectedFiles.length > 0) {
      const firstFile = selectedFiles[0];
      const fileName = firstFile?.title || firstFile?.name;
      const fileType = getFileTypeFromRecord(firstFile);
      return { fileName, fileType };
    }
    // Fallback to selectedFile prop (first file selected)
    if (selectedFile) {
      const fileName = selectedFile?.title || selectedFile?.name || selectedFile?.record?.title || selectedFile?.record?.name;
      const fileType = getFileTypeFromRecord(selectedFile?.record || selectedFile);
      return { fileName, fileType };
    }
    return { fileName: null, fileType: null };
  }, [selectedFiles, selectedFile]);

  // Get the file type (extension) of the first selected file (for backward compatibility)
  const getFirstFileType = useMemo(() => {
    return getFirstFileInfo.fileType;
  }, [getFirstFileInfo]);

  // Generate merge instruction message
  const getMergeInstructionMessage = useMemo(() => {
    const { fileName, fileType } = getFirstFileInfo;
    if (!fileName || !fileType) return null;

    let fileTypeLabel = '';
    if (fileType === 'pdf') {
      fileTypeLabel = 'PDF';
    } else if (fileType === 'docx' || fileType === 'doc') {
      fileTypeLabel = 'DOCX';
    } else if (fileType === 'xlsx' || fileType === 'xls') {
      fileTypeLabel = 'XLSX';
    } else {
      return null; // Don't show message for unsupported types
    }

    return `Only ${fileTypeLabel} files can be merged with ${fileName}.`;
  }, [getFirstFileInfo]);

  // Filter items based on search - global search when searchValue exists
  // Also filter by file type if first file type is available
  const filteredItems = useMemo(() => {
    let items = [];
    
    if (!searchValue.trim()) {
      // No search - show current level items
      items = currentLevelItems;
    } else {
      // Global search - search through entire tree
      const searchLower = searchValue.toLowerCase();
      items = allTreeItems.filter(item => 
        item.title && item.title.toLowerCase().includes(searchLower)
      );
    }
    
    // Filter by file type if first file type is available
    if (getFirstFileType) {
      items = items.filter(item => {
        // Always show folders
        if (Array.isArray(item.children)) {
          return true;
        }
        // For files, only show if extension matches
        const itemExtension = getFileExtension(item.title || item.name);
        return itemExtension === getFirstFileType;
      });
    }
    
    return items;
  }, [currentLevelItems, searchValue, allTreeItems, getFirstFileType]);

  // Build breadcrumb array - use initial breadcrumb from API, then append navigation path
  const breadcrumb = useMemo(() => {
    // Start with initial breadcrumb (fetched from API) or default to Matters + Matter Name
    let breadcrumbItems = initialBreadcrumb.length > 2 
      ? [...initialBreadcrumb]
      : [{ id: 0, name: 'Matters' }, { id: 'root', name: matterDetails?.matter_displayName }];
    
    // Append navigation path items (folders clicked by user)
    for (const pathItem of navigationPath) {
      breadcrumbItems.push({
        id: pathItem.id || pathItem.data?.key || pathItem.data?.record?.unique_id || `path-${breadcrumbItems.length}`,
        name: pathItem.title,
      });
    }
    
    return breadcrumbItems;
  }, [initialBreadcrumb, navigationPath, matterDetails?.matter_displayName]);

  // Handle search
  const handleSearch = (value) => {
    // Search is handled by filteredItems useMemo
  };

  const handleSearchChange = (value) => {
    setSearchValue(value);
  };

  // Handle breadcrumb click
  const handleBreadcrumbClick = (folderId, index) => {
    // Reset search when breadcrumb is clicked
    setSearchValue('');
    
    // Get the full breadcrumb array
    const fullBreadcrumb = breadcrumb;
    const lastIndex = fullBreadcrumb.length - 1;
    
    // Don't allow clicks on first (index 0) or last item
    if (index === 0 || index === lastIndex) {
      return;
    }
    
    // If clicking on Matter root (index 1), reset to root
    if (index === 1 || folderId === 'root' || folderId === '-1' || folderId === 0) {
      setNavigationPath([]);
      // Reset initialBreadcrumb to only Matters and Matter root
      if (initialBreadcrumb.length > 2) {
        setInitialBreadcrumb([
          initialBreadcrumb[0],
          initialBreadcrumb[1]
        ]);
      }
      return;
    }
    
    // Check if clicked item is in initialBreadcrumb or navigationPath
    if (index < initialBreadcrumb.length) {
      // Clicked on an item from initialBreadcrumb
      // Trim initialBreadcrumb to only include items up to and including the clicked index
      // This removes all items after the clicked one
      const trimmedBreadcrumb = initialBreadcrumb.slice(0, index + 1);
      setInitialBreadcrumb(trimmedBreadcrumb);
      
      // Clear navigationPath since we're going back to a point in initialBreadcrumb
      // The getCurrentLevelItems will navigate based on the trimmed initialBreadcrumb
      setNavigationPath([]);
    } else {
      // Clicked on an item from navigationPath
      // Trim navigationPath to that level
      const targetIndex = index - initialBreadcrumb.length;
      if (targetIndex >= 0 && targetIndex < navigationPath.length) {
        setNavigationPath(navigationPath.slice(0, targetIndex + 1));
      } else {
        setNavigationPath([]);
      }
    }
  };

  // Handle folder click - navigate into folder
  const handleFolderClick = (item) => {
    // Reset search when navigating
    setSearchValue('');
    
    // Add to navigation path - store title, id, and data
    const pathItem = {
      title: item.title,
      id: item.key || item.record?.unique_id,
      data: item
    };
    setNavigationPath([...navigationPath, pathItem]);
  };

  // Handle file click - select file
  const handleFileClick = (item) => {
    setSelectedItem(item);
    if (onFileSelect) {
      onFileSelect(item);
    }
  };

  // Handle item click
  const handleItemClick = (item) => {
    // If we're in search mode and item has fullPath, navigate to that location
    if (searchValue.trim() && item.fullPath && item.fullPath.length > 0) {
      // Clear search first
      setSearchValue('');
      
      // Build navigation path to the item's location
      const navPath = buildNavigationPathFromItem(item);
      
      // Reset initial breadcrumb to only Matters and Matter root when navigating from search
      // This ensures breadcrumb reflects the new location
      if (initialBreadcrumb.length > 2) {
        setInitialBreadcrumb([
          initialBreadcrumb[0],
          initialBreadcrumb[1]
        ]);
      }
      
      // If it's a folder, navigate into it
      if (Array.isArray(item.children)) {
        // Add the folder itself to navigation path
        setNavigationPath([...navPath, {
          title: item.title,
          id: item.key || item.record?.unique_id,
          data: item
        }]);
      } else {
        // It's a file - navigate to its parent folder and select it
        setNavigationPath(navPath);
        setSelectedItem(item);
        if (onFileSelect) {
          onFileSelect(item);
        }
      }
    } else {
      // Normal navigation mode
      // Check if it's a folder: has children array (even if empty)
      if (Array.isArray(item.children)) {
        // It's a folder - navigate
        handleFolderClick(item);
      } else {
        // It's a file - select
        handleFileClick(item);
      }
    }
  };

  const getFileIcon = (fileName) => getFileIconBySize(fileName, "document", 18);

  // Fetch breadcrumbs on initial render when modal opens (similar to Document.js)
  useEffect(() => {
    if (open) {
      // Get item_id from URL (like Document.js does) or from selectedFile
      let itemId = null;
      
      // Try to get from URL first (like Document.js line 85-86)
      if (location.pathname.includes("/matter/")) {
        const urlIds = location.pathname.split("/matter/")[1]?.split("/");
        if (urlIds && urlIds.length > 0) {
          itemId = urlIds.at(-1);
        }
      }
      
      // Fallback to selectedFile.unique_id if available
      if (!itemId && selectedFile?.unique_id) {
        itemId = selectedFile.unique_id;
      }
      
      // Create breadcrumbs from treeData if we have an item_id
      if (itemId) {
        try {
          let breadcrumbData = createBreadcrumbFromTreeData(treeData, matterDetails, itemId);
            
            // Remove the last item if it's a document (starts with 'D')
            // This is because when merging from Document.js, we want to show the parent folder, not the document itself
            if (breadcrumbData.length > 0) {
              const lastItem = breadcrumbData.at(-1);
              
              const isDocument = lastItem.id && lastItem.id.toString().startsWith('D');
              
              if (isDocument) {
                // Remove the last item (document) from breadcrumb
                breadcrumbData = breadcrumbData.slice(0, -1);
              }
            }
            
            setInitialBreadcrumb(breadcrumbData);
            
            // Don't set navigationPath on initial load
            // getCurrentLevelItems will navigate through initialBreadcrumb to show the correct folder contents
          setNavigationPath([]);
        } catch (error) {
          console.error('Failed to create breadcrumb from treeData:', error);
          // Fallback to API if treeData doesn't have the item
          dispatch(fetchBreadCrumbs({ item_id: itemId })).then(
            (response) => {
              let breadcrumbData = [
                { id: 0, name: "Matters" },
                ...(response?.payload?.data || []),
              ];
              
              // Remove the last item if it's a document (starts with 'D')
              if (breadcrumbData.length > 0) {
                const lastItem = breadcrumbData.at(-1);
                const isDocument = lastItem.id && lastItem.id.toString().startsWith('D');
                if (isDocument) {
                  breadcrumbData = breadcrumbData.slice(0, -1);
                }
              }
              
              setInitialBreadcrumb(breadcrumbData);
            setNavigationPath([]);
          }
        ).catch(() => {
          // Fallback to default breadcrumb if fetch fails
          setInitialBreadcrumb([
            { id: 0, name: "Matters" },
              { id: 'root', name: matterDetails?.matter_displayName || 'Matter' }
          ]);
        });
        }
      } else {
        // No item_id available, use default breadcrumb
        setInitialBreadcrumb([
          { id: 0, name: "Matters" },
          { id: 'root', name: matterDetails?.matter_displayName }
        ]);
      }
    }
  }, [open, dispatch, location.pathname, selectedFile, matterDetails?.matter_displayName, treeData]);

  // Reset when modal closes
  useEffect(() => {
    if (!open) {
      setSearchValue('');
      setNavigationPath([]);
      setSelectedItem(null);
      setInitialBreadcrumb([]);
    }
  }, [open]);

  // Update selected item when prop changes
  useEffect(() => {
    setSelectedItem(selectedFile);
  }, [selectedFile]);

  // Handle proceed button click
  const handleProceed = () => {
    if (onProceed && selectedItem) {
      onProceed(selectedItem);
    }
  };

  // Handle cancel button click
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    onClose();
  };

  if (!open) return null;

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose} aria-label="Close">
          <X size={20} />
        </CloseButton>

        <ModalTitle>{title}</ModalTitle>
        
        {getMergeInstructionMessage && (
          <MergeInstructionText>{getMergeInstructionMessage}</MergeInstructionText>
        )}

        <SearchSection>
          <Search
            searchPlaceholder={searchPlaceholder}
            isLoading={false}
            handleSearch={handleSearch}
            handleSearchOnChange={handleSearchChange}
            inputValue={searchValue}
            searchbarwidth="100%"
          />
        </SearchSection>

        <BreadcrumbSection>
          <Breadcrumb
            breadcrumb={breadcrumb}
            onBreadcrumbClick={handleBreadcrumbClick}
          />
        </BreadcrumbSection>

        <TreeSection>
          <ListContainer>
            {filteredItems.length <1 ? (
              <EmptyState>No items found</EmptyState>
            ) : (
              filteredItems.length>0 && filteredItems.map((item, index) => {
                const isFolder = Array.isArray(item.children);
                const isSelected = selectedItem && selectedItem.title === item.title;
                return (
                  <>
                 {Object.keys(item).length === 0? <EmptyState>No items found</EmptyState>:
                  <ListItem
                    key={`${item.title}-${index}`}
                    onClick={() => handleItemClick(item)}
                    isSelected={isSelected}
                  >
                    <ListItemContent>
                      <ListItemIcon>
                        {isFolder ? (
                          <FolderSimplePlus size={18} color="#5A6C87" />
                        ) : (
                          getFileIcon(item.title)
                        )}
                      </ListItemIcon>
                      <ListItemName data-testid="list-item-name">{item.title}{isFolder? <AngularExpandIcon>{">"}</AngularExpandIcon>:''}</ListItemName>
                      
                    </ListItemContent>
                  </ListItem>
              }
                  </>
                );
              })
            )}
          </ListContainer>
        </TreeSection>

        <Actions>
          <CancelButton onClick={handleCancel}>Cancel</CancelButton>
          <ProceedButton
            onClick={handleProceed}
            disabled={!selectedItem}
          >
            Proceed
          </ProceedButton>
        </Actions>
      </Modal>
    </Overlay>
  );
};

FileMergeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  treeData: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      children: PropTypes.array,
    })
  ),
  selectedFile: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  selectedFiles: PropTypes.arrayOf(PropTypes.object),
  onFileSelect: PropTypes.func,
  onProceed: PropTypes.func,
  onCancel: PropTypes.func,
  searchPlaceholder: PropTypes.string,
};

export default FileMergeModal;


